// =====================================================================
// commande-prete/useGardeFocus.js — Commandes prêtes (revue 17/10, 12e-14e passes)
// =====================================================================
// UN garde du focus, partagé par les deux écrans (liste /commande-prete et écran PDA /commande-prete/:id) :
// le lecteur du PDA « tape » dans l'élément qui a le focus — un focus perdu, c'est un scan perdu. Quand
// l'élément focalisé de l'écran disparaît (question fermée, ligne retirée, « ✓ Vu » / « Marquer imprimé » des
// rendez-vous et documents…) ou devient inutilisable (désactivé lui-même, ou par un <fieldset disabled> pendant
// un envoi / une suppression ; #ord / #cli figés quand la commande devient un essai) ET que le focus est tombé
// sur <body>, l'écran place le focus sur SA cible sûre (`onTombe`).
// (14e revue) Détection au niveau du DOCUMENT, indépendante des rendus React : focusin / focusout (capture) sur
// le conteneur, et un MutationObserver (retrait de nœuds, attribut `disabled`) pour un élément retiré ou désactivé
// SANS focusout (Chrome ne déclenche pas toujours blur au retrait). Les composants enfants sont couverts.
// Jamais volé à un champ actif en saisie ; un tap dans le vide (focusout vers rien, élément toujours là) n'arme
// rien ; un défilement au doigt ne touche pas au focus, donc à rien.
// Fonctions PURES (inutilisable, focusTombe) et brancherGarde (environnement injectable) : testées par
// frontend/test/commande-prete.test.mjs.
// =====================================================================

import { useCallback, useRef } from 'react';

/** L'élément est-il inutilisable pour le lecteur ? Retiré du document, ou désactivé — lui-même, ou par un
 *  <fieldset disabled> ancêtre (`:disabled`). */
export const inutilisable = (el) => !!el && (el.isConnected === false || el.disabled === true
  || (typeof el.matches === 'function' && el.matches(':disabled')));

/** Le focus est-il TOMBÉ ? `perdu` = l'élément de l'écran qui avait le focus, `actif` = document.activeElement,
 *  `body` = document.body. Vrai seulement si `perdu` est inutilisable ET que le focus n'est sur AUCUN autre
 *  élément : <body>, nulle part, ou resté sur `perdu` désactivé (selon le navigateur). Un autre élément qui a
 *  le focus (un champ actif en saisie) : jamais. */
export const focusTombe = (actif, perdu, body) => inutilisable(perdu) && (!actif || actif === body || actif === perdu);

/**
 * Branche le garde sur `conteneur` ; rend la fonction qui le débranche. `onTombe()` est appelé une fois par chute,
 * après coup (setTimeout 0 : le focus voulu par l'écran — useLayoutEffect, setTimeout déjà prévu — passe avant).
 * `env` (document, MutationObserver, setTimeout, clearTimeout) : le navigateur, ou un faux pour les tests.
 */
export function brancherGarde(conteneur, onTombe, env = globalThis) {
  const doc = env.document;
  // L'élément de l'écran qui a le focus (ou vient de le perdre vers rien) : le seul surveillé.
  let suivi = doc.activeElement && conteneur.contains(doc.activeElement) ? doc.activeElement : null;
  let prevu = null;
  const verifier = () => {
    prevu = null;
    const el = suivi;
    if (!el) return;
    const actif = doc.activeElement;
    if (focusTombe(actif, el, doc.body)) { suivi = null; onTombe(); return; }
    if (actif !== el) suivi = null;   // parti exprès (tap dans le vide, autre élément) : plus rien à rattraper
  };
  const planifier = () => { if (prevu === null) prevu = env.setTimeout(verifier, 0); };
  const focusin = (e) => { suivi = e.target; };
  // Vers un autre élément : il le dira (focusin) ; vers rien : on vérifie après coup.
  const focusout = (e) => { if (e.relatedTarget) { suivi = null; return; } suivi = e.target; planifier(); };
  const observateur = new env.MutationObserver(() => { if (suivi) planifier(); });
  conteneur.addEventListener('focusin', focusin);
  conteneur.addEventListener('focusout', focusout, true);
  observateur.observe(conteneur, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
  return () => {
    conteneur.removeEventListener('focusin', focusin);
    conteneur.removeEventListener('focusout', focusout, true);
    observateur.disconnect();
    if (prevu !== null) env.clearTimeout(prevu);
    prevu = null; suivi = null;
  };
}

/**
 * Garde du focus d'un écran : rend la `ref` à poser sur son conteneur (branché à l'affichage du conteneur,
 * débranché à son retrait). `onTombe()` place le focus sur la cible sûre de l'écran — toujours sa dernière
 * version (valeurs du dernier rendu), sans rebrancher à chaque rendu.
 */
export function useGardeFocus(onTombe) {
  const rappel = useRef(onTombe);
  rappel.current = onTombe;
  const debrancher = useRef(null);
  return useCallback((conteneur) => {
    if (debrancher.current) { debrancher.current(); debrancher.current = null; }
    if (conteneur) debrancher.current = brancherGarde(conteneur, () => rappel.current());
  }, []);
}
